import React, { useCallback, useMemo } from 'react'
import {
    Box,
    Button,
    Collapse,
    Stack,
    Typography,
} from '@mui/material'
import SimpleBar from 'simplebar-react'
import AiSparkIcon from '../../assets/icons/AiSparkIcon'
import HistoryIcon from '../../assets/icons/History'
import PlusIcon from '../../assets/icons/PlusIcon'
import { NonInteractiveTooltip } from '../custom'
import 'simplebar-react/dist/simplebar.min.css'
import ChatComposer from '../screener/aiChat/ChatComposer'
import AiAssistantEmptyState from './AiAssistantEmptyState'
import ChatHistoryPanel from '../screener/aiChat/ChatHistoryPanel'
import ChatTurn from '../screener/aiChat/ChatTurn'
import { chartCommandResultsFooter } from '../chartV2/aiChat/chartCommandResultsFooter'
import { filterSemanticKey } from '../screener/aiChat/conversationFilters'
import { useChatPanelAutoScroll } from '../screener/aiChat/useChatPanelAutoScroll'
import type { ChatTurnMessage } from '../screener/aiChat/types'
import { useAiAssistant } from './AiAssistantContext'

function isLastAppliedFilterTurn(message: ChatTurnMessage, messages: ChatTurnMessage[]): boolean {
    let last: ChatTurnMessage | undefined
    for (const m of messages) {
        if (m.role === 'assistant' && m.filterStatus === 'applied' && m.turnId && m.filters?.length) {
            last = m
        }
    }
    return last?.id === message.id
}

function undoStateForMessage(
    message: ChatTurnMessage,
    messages: ChatTurnMessage[],
    activeLedger: ReturnType<typeof useAiAssistant>['ledgerRef']['current'],
    runInFlight: boolean,
): { canUndo: boolean; reason?: string } {
    if (message.filterStatus !== 'applied' || !message.turnId || !message.filters?.length) {
        return { canUndo: false }
    }
    if (runInFlight) return { canUndo: false, reason: 'Wait for the current run to finish' }
    if (!isLastAppliedFilterTurn(message, messages)) return { canUndo: false }
    const deltaKeys = new Set(message.filters.map(filterSemanticKey))
    const ledgerKeys = new Set(activeLedger.map(filterSemanticKey))
    if (![...deltaKeys].some((key) => ledgerKeys.has(key))) return { canUndo: false }
    return { canUndo: true }
}

export default function AiAssistantPanel() {
    const {
        conversationId,
        messages,
        input,
        setInput,
        loading,
        error,
        conversations,
        historyOpen,
        setHistoryOpen,
        onSend,
        onRetry,
        onAbort,
        onNewChat,
        switchConversation,
        refreshConversations,
        onUndoTurnFilters,
        undoInFlight,
        userName,
        userAvatarUrl,
        ledgerRef,
    } = useAiAssistant()

    const isCurrentChatEmpty = messages.length === 0 && !loading && !error

    const { scrollableNodeProps, contentRef } = useChatPanelAutoScroll({
        messages,
        loading,
        conversationId,
    })

    const headerTitle = useMemo(() => {
        const title = conversations.find((c) => c.id === conversationId)?.title?.trim()
        return title || 'New chat'
    }, [conversationId, conversations])

    const onToggleHistory = useCallback(() => {
        setHistoryOpen(!historyOpen)
        if (!historyOpen) void refreshConversations()
    }, [historyOpen, refreshConversations, setHistoryOpen])

    const renderedMessages = useMemo(() => {
        const items: React.ReactNode[] = []
        // A user message opens a new exchange, so it gets the wider gap; the
        // assistant reply stays visually tied to the prompt above it.
        let isFirstRendered = true

        for (let i = 0; i < messages.length; i++) {
            const message = messages[i]
            if (message.routingOnly) continue
            const undo =
                message.role === 'assistant'
                    ? undoStateForMessage(message, messages, ledgerRef.current, loading)
                    : { canUndo: false as const }

            const gapTop = isFirstRendered ? 0 : message.role === 'user' ? 3 : 1.75
            isFirstRendered = false

            items.push(
                <Box key={message.id} sx={{ mt: gapTop }}>
                    <ChatTurn
                        message={message}
                        runInFlight={loading}
                        userAvatarUrl={userAvatarUrl}
                        userName={userName}
                        onUndoFilters={message.filters?.length ? onUndoTurnFilters : undefined}
                        undoDisabled={!undo.canUndo || undoInFlight}
                        undoDisabledReason={undo.reason}
                        onRetry={message.showRetry && message.retryPrompt ? onRetry : undefined}
                        retryDisabled={loading}
                        footer={chartCommandResultsFooter(message.content, message.chartCommandResults)}
                    />
                </Box>,
            )
        }
        return items
    }, [
        ledgerRef,
        loading,
        messages,
        onRetry,
        onUndoTurnFilters,
        undoInFlight,
        userAvatarUrl,
        userName,
    ])

    return (
        <Stack sx={{ height: '100%', minHeight: 0 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                px={1}
                py={0}
                borderBottom={1}
                borderColor="divider"
                sx={{ flexShrink: 0, minHeight: 40 }}
            >
                <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
                    <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                        <AiSparkIcon />
                    </Box>
                    <Typography variant="body1" fontWeight={500} noWrap sx={{ minWidth: 0 }}>
                        {headerTitle}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <NonInteractiveTooltip title={historyOpen ? 'Hide history' : 'Chat history'} arrow placement="top">
                        <Button
                            variant="outlined"
                            color="inherit"
                            size="small"
                            onClick={onToggleHistory}
                            aria-label="chat history"
                            sx={{
                                minWidth: '32px',
                                ...(historyOpen && { color: 'primary.main' }),
                            }}
                        >
                            <HistoryIcon fontSize="small" />
                        </Button>
                    </NonInteractiveTooltip>
                    <NonInteractiveTooltip title="New chat" arrow placement="top">
                        <span>
                            <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                onClick={onNewChat}
                                aria-label="new chat"
                                disabled={isCurrentChatEmpty}
                                sx={{ minWidth: '32px' }}
                            >
                                <PlusIcon fontSize="small" />
                            </Button>
                        </span>
                    </NonInteractiveTooltip>
                </Stack>
            </Stack>

            <Collapse in={historyOpen} unmountOnExit>
                <Box>
                    <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                        <ChatHistoryPanel
                            conversations={conversations}
                            activeConversationId={conversationId}
                            onSelect={switchConversation}
                        />
                    </Box>
                    <Box
                        sx={{
                            height: 'var(--extra-small-spacing)',
                            backgroundColor: (theme) => theme.palette.background.default,
                        }}
                    />
                </Box>
            </Collapse>

            <Box flex={1} sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SimpleBar style={{ flex: 1, maxHeight: '100%' }} scrollableNodeProps={scrollableNodeProps}>
                    <Box ref={contentRef} sx={{ px: 2, py: 1.5 }}>
                        {isCurrentChatEmpty ? (
                            <AiAssistantEmptyState
                                onSelectPrompt={(text) => onSend(text)}
                                disabled={loading}
                            />
                        ) : (
                            <>
                                {renderedMessages}
                            </>
                        )}
                        {error && (
                            <Typography variant="body2" color="error.main" sx={{ mt: 2 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                </SimpleBar>
            </Box>

            <Box sx={{ px: 1.5, py: 1.25, flexShrink: 0 }}>
                <ChatComposer
                    input={input}
                    onInputChange={setInput}
                    onSend={() => onSend()}
                    onAbort={onAbort}
                    loading={loading}
                    canStop={loading}
                    disabled={false}
                    placeholder="Describe what you want to see"
                />
            </Box>
        </Stack>
    )
}
